/**
 * ReferencePersonCard Component
 *
 * Displays a reference person (AI Avatar) card with:
 * - Main image + 3 vertical thumbnails
 * - Name and description
 * - Status badges:
 *   - "Active" (lime green) - shows when person is selected as default for generation
 *   - "Training" (yellow/amber) - shows when person has all required images uploaded
 * - Progress bar for image count
 * - Action buttons: Add Images, Edit, Delete
 */

import React, { useCallback } from 'react';
import { Images, Pencil, Trash2, User } from 'lucide-react';
import type { IReferencePersonCardProps } from '@/@types/referencePersons';
import { isPersonActive } from '@/@types/referencePersons';
import { cn } from '@/lib/utils';

function ReferencePersonCard({
  person,
  onEdit,
  onDelete,
  onViewImages,
  onSetDefault,
  maxImages,
}: IReferencePersonCardProps) {
  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEdit();
    },
    [onEdit]
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete();
    },
    [onDelete]
  );

  const handleViewImages = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onViewImages();
    },
    [onViewImages]
  );

  const handleSetDefault = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSetDefault();
    },
    [onSetDefault]
  );

  const imageCount = person.imageIds.length;
  const isActive = isPersonActive(imageCount, maxImages);
  const progressPercentage = (imageCount / maxImages) * 100;

  // Get first 4 images for display (1 main + 3 thumbnails)
  const mainImage = person.imageUrls[0];
  const thumbnails = person.imageUrls.slice(1, 4);

  return (
    <div
      className={cn(
        'w-full min-h-[374px] rounded-xl overflow-hidden flex flex-col',
        'bg-card border border-border'
      )}
    >
      {/* Image Section - 200px height */}
      <div className="h-[200px] p-3 flex gap-2 flex-shrink-0 bg-muted rounded-t-xl">
        {/* Main Image */}
        <div className="w-[180px] h-full rounded-lg overflow-hidden bg-border">
          {mainImage ? (
            <img
              src={mainImage}
              alt={`${person.name} main`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <User className="w-12 h-12 text-muted-foreground opacity-50" />
            </div>
          )}
        </div>

        {/* Thumbnail Column */}
        <div className="flex-1 flex flex-col gap-2">
          {[0, 1, 2].map(slotIndex => (
            <div
              key={`thumbnail-slot-${slotIndex}`}
              className="flex-1 rounded-md overflow-hidden bg-border"
            >
              {thumbnails[slotIndex] ? (
                <img
                  src={thumbnails[slotIndex]}
                  alt={`${person.name} thumbnail ${slotIndex + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {/* Info Section */}
      <div className="p-4 flex flex-col gap-3 flex-1">
        {/* Header Row: Name + Status Badges */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-foreground truncate">{person.name}</h3>
            {person.description && (
              <p className="text-[13px] text-muted-foreground line-clamp-2">{person.description}</p>
            )}
          </div>

          {/* Status Badges */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Active badge - shows when person is selected as default */}
            {person.isDefault && (
              <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-primary text-primary-foreground">
                Active
              </span>
            )}
            {/* Training badge - shows when person has all required images */}
            {isActive && (
              <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-[#2E2A1A] text-[#FBBF24] light:bg-[#FEF3C7] light:text-[#92400E]">
                Training
              </span>
            )}
          </div>
        </div>

        {/* Progress Section */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">Reference Images</span>
            <span className="text-xs font-semibold text-primary light:text-primary-foreground">
              {imageCount}/{maxImages}
            </span>
          </div>
          <div className="h-1.5 rounded-sm bg-border overflow-hidden">
            <div
              className="h-full rounded-sm bg-primary transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Actions Section - All buttons in one row */}
        <div className="flex gap-2 pt-2">
          {/* Add Images Button */}
          <button
            type="button"
            onClick={handleViewImages}
            className="flex-1 h-10 rounded-lg flex items-center justify-center gap-2 bg-muted hover:bg-border transition-colors duration-150"
          >
            <Images className="w-4 h-4 text-foreground" />
            <span className="text-[13px] font-medium text-foreground">Add Images</span>
          </button>

          {/* Edit Button */}
          <button
            type="button"
            onClick={handleEdit}
            className="w-10 h-10 rounded-lg flex items-center justify-center bg-muted hover:bg-border transition-colors duration-150"
          >
            <Pencil className="w-4 h-4 text-muted-foreground" />
          </button>

          {/* Delete Button */}
          <button
            type="button"
            onClick={handleDelete}
            className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#2A1A1A] light:bg-[#FEE2E2] hover:bg-[#3A2020] light:hover:bg-[#FECACA] transition-colors duration-150"
          >
            <Trash2 className="w-4 h-4 text-[#EF7070] light:text-[#DC2626]" />
          </button>
        </div>

        {/* Set as Active - separate row, only show if person has all images and not already active */}
        {isActive && !person.isDefault && (
          <button
            type="button"
            onClick={handleSetDefault}
            className="w-full h-10 rounded-lg flex items-center justify-center bg-primary hover:bg-primary/90 transition-colors duration-150"
          >
            <span className="text-[13px] font-medium text-primary-foreground">Set as Active</span>
          </button>
        )}
      </div>
    </div>
  );
}

export { ReferencePersonCard };
